import {
  InfoOutlined as InfoOutlinedIcon,
  PersonAdd as PersonAddIcon,
  Refresh as RefreshIcon,
  Upload as UploadIcon,
} from '@mui/icons-material';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
} from '@mui/x-data-grid';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { useDirection } from '../../context/DirectionContext';
import { translate as tr } from '../../i18n/translations';
import apiService from '../../services/api';
import { User, UserRole } from '../../types/api';
import { getErrorMessage } from '../../utils/errorUtils';
import ErrorAlert from '../Common/ErrorAlert';
import LoadingSpinner from '../Common/LoadingSpinner';

interface TeamAssignmentsTableProps {
  cycleId: string;
}

const CYCLE_ROLES = [
  UserRole.TEAMLEAD,
  UserRole.ASSESSOR,
  UserRole.SOCIO,
  UserRole.INTERVIEWER,
  UserRole.VIEWER,
];

const TeamAssignmentsTable: React.FC<TeamAssignmentsTableProps> = ({ cycleId }) => {
  const queryClient = useQueryClient();
  const { lang } = useDirection();
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [addUser, setAddUser] = useState<User | null>(null);
  const [addTeamNum, setAddTeamNum] = useState('');
  const [addRole, setAddRole] = useState<UserRole | ''>('');
  const [addError, setAddError] = useState('');

  const { data: teamAssignments, isLoading, error: queryError, refetch } = useQuery({
    queryKey: ['teamAssignments', cycleId],
    queryFn: () => apiService.getTeamAssignments(cycleId),
    enabled: !!cycleId,
  });

  const { data: users } = useQuery({
    queryKey: ['users'],
    queryFn: () => apiService.getUsers(),
  });

  const uploadMutation = useMutation({
    mutationFn: ({ cycleId, file }: { cycleId: string; file: File }) =>
      apiService.uploadUserTeamCSV(cycleId, file),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['teamAssignments'] });
      refetch();
      setSuccessMsg(data.message);
      setError('');
    },
    onError: (err: any) => {
      setError(getErrorMessage(err));
      setSuccessMsg('');
    },
  });

  const assignMutation = useMutation({
    mutationFn: (assignments: { userId: string; teamNumber: number; role: UserRole }[]) =>
      apiService.assignToTeams(cycleId, { assignments }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teamAssignments'] });
      refetch();
      setAddOpen(false);
      setAddUser(null);
      setAddTeamNum('');
      setAddRole('');
      setAddError('');
      setSuccessMsg(tr('team.addedSuccess', lang));
    },
    onError: (err: any) => setAddError(getErrorMessage(err)),
  });

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !cycleId) return;
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setError(tr('team.selectCsv', lang));
      return;
    }
    uploadMutation.mutate({ cycleId, file });
    e.target.value = '';
  };

  const handleAddSubmit = () => {
    if (!addUser || !addTeamNum || !addRole) {
      setAddError(tr('team.allFieldsRequired', lang));
      return;
    }
    const teamNum = parseInt(addTeamNum, 10);
    if (isNaN(teamNum) || teamNum < 1) {
      setAddError(tr('ta.teamNumberInvalid', lang));
      return;
    }
    assignMutation.mutate([{ userId: addUser.id, teamNumber: teamNum, role: addRole }]);
  };

  const getRoleColor = (role: UserRole) => {
    switch (role) {
      case UserRole.TEAMLEAD: return 'primary';
      case UserRole.ASSESSOR: return 'success';
      case UserRole.SOCIO: return 'info';
      case UserRole.INTERVIEWER: return 'warning';
      default: return 'default';
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'userEmail', headerName: tr('team.col.email', lang), width: 220,
      renderCell: (p: GridRenderCellParams) => p.row.user?.email || 'N/A',
    },
    {
      field: 'userName', headerName: tr('team.col.name', lang), width: 180,
      renderCell: (p: GridRenderCellParams) =>
        p.row.user ? `${p.row.user.firstName} ${p.row.user.lastName}` : '-',
    },
    {
      field: 'role', headerName: tr('team.col.role', lang), width: 130,
      renderCell: (p: GridRenderCellParams) => (
        <Chip label={p.row.role || 'N/A'} color={getRoleColor(p.row.role)} size="small" />
      ),
    },
    {
      field: 'teamNumber', headerName: tr('team.col.teamNum', lang), width: 100,
      renderCell: (p: GridRenderCellParams) => p.value ?? '-',
    },
  ];

  if (isLoading) return <LoadingSpinner message={tr('team.loading', lang)} />;
  if (queryError) return <ErrorAlert error={queryError} title={tr('team.errorLoad', lang)} />;

  const gridSx = {
    border: 'none',
    '& .MuiDataGrid-columnHeaders': { bgcolor: '#F8FAFC', borderBottom: '1px solid #E2E8F0' },
    '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: '#64748B' },
    '& .MuiDataGrid-row:hover': { bgcolor: '#F8FAFC' },
    '& .MuiDataGrid-cell': { borderBottom: '1px solid #F1F5F9', fontSize: '0.875rem' },
    '& .MuiDataGrid-footerContainer': { borderTop: '1px solid #E2E8F0' },
    '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
  };

  return (
    <div>
      <Alert severity="info" icon={<InfoOutlinedIcon fontSize="small" />} sx={{ mb: 2 }}>{tr('ta.cannotRemove', lang)}</Alert>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {successMsg && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMsg('')}>{successMsg}</Alert>}

      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#0F172A' }}>
          {tr('cd.tab.team', lang)}
          <Typography component="span" sx={{ ml: 1, fontWeight: 400, color: '#94A3B8', fontSize: '0.85rem' }}>
            {teamAssignments?.length ?? 0}
          </Typography>
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            size="small"
            onClick={() => { setAddOpen(true); setAddError(''); }}
          >
            {tr('team.addAssignment', lang)}
          </Button>
          <input accept=".csv" style={{ display: 'none' }} id="user-roles-csv-upload-input" type="file" onChange={handleFileUpload} />
          <Tooltip title={tr('team.csvHint', lang)} arrow placement="top">
            <label htmlFor="user-roles-csv-upload-input">
              <Button variant="outlined" component="span" startIcon={<UploadIcon />} size="small" disabled={uploadMutation.isPending}>
                {uploadMutation.isPending ? tr('common.uploading', lang) : tr('team.uploadCsv', lang)}
              </Button>
            </label>
          </Tooltip>
          <Button variant="outlined" startIcon={<RefreshIcon />} size="small" onClick={() => refetch()}>
            {tr('common.refresh', lang)}
          </Button>
        </Box>
      </Box>

      <Paper sx={{ width: '100%', borderRadius: 3, overflow: 'hidden', border: '1px solid #E2E8F0' }}>
        <DataGrid
          rows={teamAssignments ?? []}
          columns={columns}
          getRowId={row => `team-${row.teamNumber}-${row.user?.id ?? row.user?.email}`}
          pageSizeOptions={[10, 25, 50]}
          disableRowSelectionOnClick
          autoHeight
          sx={gridSx}
        />
      </Paper>

      {/* Add Assignment Dialog */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{tr('team.addAssignment', lang)}</DialogTitle>
        <DialogContent sx={{ pt: '16px !important', display: 'flex', flexDirection: 'column', gap: 2 }}>
          {addError && <Alert severity="error">{addError}</Alert>}
          <Autocomplete
            options={users ?? []}
            getOptionLabel={u => `${u.firstName} ${u.lastName} (${u.email})`}
            value={addUser}
            onChange={(_, v) => setAddUser(v)}
            renderInput={p => <TextField {...p} label={tr('team.userLabel', lang)} size="small" />}
          />
          <TextField
            label={tr('team.teamNumber', lang)}
            size="small"
            type="number"
            value={addTeamNum}
            onChange={e => setAddTeamNum(e.target.value)}
            inputProps={{ min: 1 }}
          />
          <FormControl size="small" fullWidth>
            <InputLabel>{tr('common.role', lang)}</InputLabel>
            <Select value={addRole} label={tr('common.role', lang)} onChange={e => setAddRole(e.target.value as UserRole)}>
              {CYCLE_ROLES.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>{tr('common.cancel', lang)}</Button>
          <Button variant="contained" onClick={handleAddSubmit} disabled={assignMutation.isPending}>
            {assignMutation.isPending ? tr('common.saving', lang) : tr('common.add', lang)}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default TeamAssignmentsTable;
